import { Box } from '@mui/material';
import { useCallback, useLayoutEffect, useRef } from 'react';
import { useScrollContainer } from '../resizable-split-layout/scroll-container.context';
import { MessagesListProps } from './messages-list.interface';

const BOTTOM_THRESHOLD_PX = 48;

export function MessagesListComponent({ children }: MessagesListProps) {
  const scrollContainer = useScrollContainer();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const lastScrollTopRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    if (!scrollContainer || !stickToBottomRef.current) {
      return;
    }

    scrollContainer.scrollTop = scrollContainer.scrollHeight;
    lastScrollTopRef.current = scrollContainer.scrollTop;
  }, [scrollContainer]);

  useLayoutEffect(() => {
    scrollToBottom();
  });

  useLayoutEffect(() => {
    const content = contentRef.current;

    if (!content || !scrollContainer) {
      return undefined;
    }

    const handleScroll = () => {
      const distanceToBottom =
        scrollContainer.scrollHeight -
        scrollContainer.scrollTop -
        scrollContainer.clientHeight;
      const atBottom = distanceToBottom <= BOTTOM_THRESHOLD_PX;
      const scrolledUp = scrollContainer.scrollTop + 1 < lastScrollTopRef.current;

      if (atBottom) {
        stickToBottomRef.current = true;
      } else if (scrolledUp) {
        stickToBottomRef.current = false;
      }

      lastScrollTopRef.current = scrollContainer.scrollTop;
    };

    scrollContainer.addEventListener('scroll', handleScroll);

    const handleContentChange = () => {
      scrollToBottom();
    };

    const resizeObserver = new ResizeObserver(handleContentChange);
    resizeObserver.observe(content);

    const mutationObserver = new MutationObserver(handleContentChange);
    mutationObserver.observe(content, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [scrollContainer, scrollToBottom]);

  return (
    <Box
      ref={contentRef}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        py: 3,
        px: 2,
      }}
    >
      {children}
    </Box>
  );
}
